import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PrimeNG } from 'primeng/config';

// PrimeNG locale definitions for calendar/datepicker
const PRIMENG_LOCALES: Record<string, any> = {
	es: {
		firstDayOfWeek: 1,
		dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
		dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
		dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
		monthNames: [
			'enero',
			'febrero',
			'marzo',
			'abril',
			'mayo',
			'junio',
			'julio',
			'agosto',
			'septiembre',
			'octubre',
			'noviembre',
			'diciembre',
		],
		monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
		today: 'Hoy',
		clear: 'Limpiar',
		weekHeader: 'Sm',
		dateFormat: 'dd/mm/yy',
	},
};

/**
 * Service responsible for initializing the application language during startup.
 * This runs as an APP_INITIALIZER before the application renders.
 */
@Injectable({
	providedIn: 'root',
})
export class LanguageInitService {
	private readonly defaultLanguage = 'es';
	private transloco = inject(TranslocoService);
	private primeNG = inject(PrimeNG);

	/**
	 * Initializes the application language.
	 * Called during APP_INITIALIZER phase.
	 * ERIS is monolingual: stored legacy preferences must never switch the UI
	 * away from Spanish.
	 */
	async initializeLanguage(): Promise<void> {
		const langToSet = this.defaultLanguage;

		// Set the language
		this.transloco.setActiveLang(langToSet);

		// Set PrimeNG locale for datepicker/calendar
		this.updatePrimeNGLocale(langToSet);

		// Subscribe to language changes to update PrimeNG locale
		this.transloco.langChanges$.subscribe((lang) => {
			this.updatePrimeNGLocale(lang);
		});

		// Wait for translations to be loaded
		await this.waitForTranslations(langToSet);
	}

	/**
	 * Update PrimeNG translation/locale settings
	 */
	private updatePrimeNGLocale(lang: string): void {
		const locale = PRIMENG_LOCALES[lang] || PRIMENG_LOCALES['es'];
		this.primeNG.setTranslation(locale);
	}

	/**
	 * Wait for translations to be loaded for a specific language
	 */
	private async waitForTranslations(lang: string): Promise<void> {
		return new Promise((resolve) => {
			// Check if translations are already loaded
			const existingTranslation = this.transloco.getTranslation(lang);
			if (existingTranslation && Object.keys(existingTranslation).length > 0) {
				resolve();
				return;
			}

			// Load the translations explicitly
			let subscription: { unsubscribe: () => void } | null = null;
			let resolved = false;

			const cleanup = () => {
				if (!resolved) {
					resolved = true;
					subscription?.unsubscribe();
					resolve();
				}
			};

			subscription = this.transloco.load(lang).subscribe({
				next: () => cleanup(),
				error: () => cleanup(),
			});

			// Safety timeout (3 seconds)
			setTimeout(cleanup, 3000);
		});
	}
}
